import { CellContext, ColumnDef, FilterFn } from '@tanstack/react-table';
import { ILxsBillingDocument } from '../../../actions/get-billing-documents.action';
import { NO_DATA_FALLBACK } from '../../../configuration';
import {
    LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE,
    LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE,
    LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE_TEXT,
    LXS_BILLING_DOCUMENTS_TABLE_DOCUMENT_ID,
    LXS_BILLING_DOCUMENTS_TABLE_DUE_DATE,
    LXS_BILLING_DOCUMENTS_TABLE_ORDER_NUMBER,
    LXS_BILLING_DOCUMENTS_TABLE_PURCHASE_ORDER_NUMB_EXT,
    LXS_BILLING_DOCUMENTS_TABLE_SHIP_TO,
    LXS_BILLING_DOCUMENTS_TABLE_SHIP_TO_NAME,
    LXS_BILLING_DOCUMENTS_TABLE_SOLD_TO_NAME,
    LXS_BILLING_DOCUMENTS_TABLE_TOTAL_AMOUNT
} from '../../../configuration/billing-documents';
import { ILocalizationState } from '../../../states/localization-state';
import { dateLocaleFormatter, formatCurrency } from '../../../utilities';
import { displayTableCellValue } from '../../../utilities/display-table-cell-value';
import { mapBillingTypeToText } from '../../../utilities/map-billing-type-to-text';
import { ILxsBillingDocumentsResources } from '../lxs-billing-documents.props.autogenerated';

/**
Factory to build columns for the billing documents table.
*/
export const getBillingDocumentColumns = (
    resources: ILxsBillingDocumentsResources,
    localizationState: ILocalizationState,
    globalFilter: string,
    data: ILxsBillingDocument[]
): ColumnDef<ILxsBillingDocument, any>[] => {
    const defaultCellRenderer = (globalFilterValue: string) => (info: CellContext<ILxsBillingDocument, any>) =>
        displayTableCellValue(info.getValue(), globalFilterValue);

    const dateCellRenderer = (localization: ILocalizationState) => (info: CellContext<ILxsBillingDocument, any>) =>
        info.getValue() ? dateLocaleFormatter(localization).formatDate(info.getValue()) : NO_DATA_FALLBACK;

    const currencyCellRenderer = (localization: ILocalizationState) => (info: CellContext<ILxsBillingDocument, any>) => {
        const { Currency } = info.row.original || {};
        const value = info.getValue();
        return value !== undefined && value !== null ? formatCurrency(localization, Currency, Number(value)) : NO_DATA_FALLBACK;
    };

    const dateRangeFilter: FilterFn<any> = (row, columnId, value) => {
        const currentValue = row.getValue<string>(columnId);
        const [from, to] = value;
        return (
            (!from || new Date(from).getTime() <= new Date(currentValue.split('T')[0]).getTime()) &&
            (!to || new Date(currentValue.split('T')[0]).getTime() <= new Date(to).getTime())
        );
    };

    /**
     * Filter function for the billing-type column.
     * @param {any} row - The current row object from react-table.
     * @param {string} columnId - The id of the column to filter.
     * @param {string | string[]} value - Filter value(s). Can be an array of strings or a comma-separated string.
     * @returns {boolean} True if the row matches any of the selected filter values (compares both code and mapped text).
     */
    const billingTypeFilter: FilterFn<any> = (row, columnId, value) => {
        const selectedValues: string[] = Array.isArray(value)
            ? value.map(v => String(v).trim()).filter(Boolean)
            : typeof value === 'string'
            ? value
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
            : [];

        return (
            selectedValues.includes(row.getValue(columnId)) || selectedValues.includes(mapBillingTypeToText(data, row.getValue(columnId)))
        );
    };

    return [
        {
            accessorFn: row => row.DocumentId,
            id: LXS_BILLING_DOCUMENTS_TABLE_DOCUMENT_ID,
            header: resources.lxsBillingDocumentsDocumentIdTitle,
            // cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id
        },
        {
            accessorFn: row => row.BillingTypeText,
            id: LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE_TEXT,
            header: resources.lxsBillingDocumentsBillingTypeTextTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id
        },
        {
            accessorFn: row => row.References?.[0]?.OrderNumber,
            id: LXS_BILLING_DOCUMENTS_TABLE_ORDER_NUMBER,
            header: resources.lxsBillingDocumentsOrderNumberTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id,
            enableHiding: true
        },
        /* {
            accessorFn: row => row.SoldToId,
            id: LXS_BILLING_DOCUMENTS_TABLE_SOLD_TO,
            header: resources.lxsBillingDocumentsSoldToTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id
        }, */
        {
            accessorFn: row => row.SoldToName,
            id: LXS_BILLING_DOCUMENTS_TABLE_SOLD_TO_NAME,
            header: resources.lxsBillingDocumentsSoldToTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id
        },
        {
            accessorFn: row => {
                return Array.from(new Set(row.References?.map(ref => ref.ShipToName).filter(Boolean) || [])).join(', ');
            },
            id: LXS_BILLING_DOCUMENTS_TABLE_SHIP_TO_NAME,
            header: resources.lxsBillingDocumentsShipToTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id,
            enableHiding: true
        },
        {
            accessorFn: row => row.BillingDate,
            id: LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE,
            header: resources.lxsBillingDocumentsBillingDateTitle,
            cell: dateCellRenderer(localizationState),
            footer: props => props.column.id,
            filterFn: dateRangeFilter
        },

        {
            accessorFn: row => row.TotalAmount,
            id: LXS_BILLING_DOCUMENTS_TABLE_TOTAL_AMOUNT,
            header: resources.lxsBillingDocumentsTotalAmountTitle,
            cell: currencyCellRenderer(localizationState),
            footer: props => props.column.id
        },
        /* {
            accessorFn: row => row.NetAmount,
            id: LXS_BILLING_DOCUMENTS_TABLE_NET_AMOUNT,
            header: resources.lxsBillingDocumentsNetAmountTitle,
            cell: currencyCellRenderer(localizationState),
            footer: props => props.column.id
        },
        {
            accessorFn: row => row.Tax,
            id: LXS_BILLING_DOCUMENTS_TABLE_TAX,
            header: resources.lxsBillingDocumentsTaxTitle,
            cell: currencyCellRenderer(localizationState),
            footer: props => props.column.id
        }, */
        {
            accessorFn: row => row.BillingType,
            id: LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE,
            header: resources.lxsBillingDocumentsBillingTypeTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id,
            enableHiding: true,
            filterFn: billingTypeFilter
        },

        {
            accessorFn: row => row.References?.[0]?.PurchaseOrderNumbExt,
            id: LXS_BILLING_DOCUMENTS_TABLE_PURCHASE_ORDER_NUMB_EXT,
            header: resources.lxsBillingDocumentsPurchaseOrderNumberExtTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id,
            enableHiding: true
        },
        {
            accessorFn: row => row.References?.[0]?.ShipTo,
            id: LXS_BILLING_DOCUMENTS_TABLE_SHIP_TO,
            header: resources.lxsBillingDocumentsShipToTitle,
            cell: defaultCellRenderer(globalFilter),
            footer: props => props.column.id,
            enableHiding: true
        },
        {
            accessorFn: row => row.DueDate,
            id: LXS_BILLING_DOCUMENTS_TABLE_DUE_DATE,
            header: resources.lxsBillingDocumentsDueDateTitle,
            cell: dateCellRenderer(localizationState),
            footer: props => props.column.id
        }
        /*
        {
            id: LXS_BILLING_DOCUMENTS_TABLE_ACTION,
            cell: defaultCellRenderer(globalFilter),
            header: () => resources.globalAction,
            footer: props => props.column.id,
            enableSorting: false
        }*/
    ];
};

/**
 * column id : shouldColumnBeVisible?
 */
export const hiddenColumns = {
    [LXS_BILLING_DOCUMENTS_TABLE_SHIP_TO]: false,
    [LXS_BILLING_DOCUMENTS_TABLE_PURCHASE_ORDER_NUMB_EXT]: false,
    [LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE]: false
};
